import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Box from '@repo/ui/box'
import { BuildingTwoIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getCenter } from '../../../../../../api/centers/get-center'
import { getCenterFinalCostVisibility } from '../../../../../../api/centers/get-center-final-cost-visibility'
import { getOlPermissions } from '../../../../../../lib/constants/ol-permissions'
import useAuth from '../../../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../../../lib/hooks/use-data-props'
import useTranslate from '../../../../../../lib/hooks/use-translate'
import { OLTypeEnum } from '../../../../../../lib/types/enums/ol/ol-type.enum'
import { Center } from '../../../../../../lib/types/models/center/center'
import { DataProps } from '../../../../../../lib/types/utils'
import { ItemNotFound } from '../../../../../elements/item-not-found'
import LoadingScreen from '../../../../../elements/loading/loading-screen'
import { CenterCosts } from '../../../../centers/center-costs'
import { getCenterCostsMode } from '../../../../centers/center-costs/utils'
import OlPersonalSpaceTeachingHallTable from '../../../../ol-personal-space/ol-personal-space-center-program/ol-personal-space-teaching-hall-table'

type Props = {
  view: 'teaching-hall' | 'addresses'
}

export function CenterSimpleProgramAndActivities(props: DataProps<Center, Props>) {
  const { t } = useTranslate()
  const { user } = useAuth()
  const { id, data, isPending, view } = useDataProps(props, (id) => getCenter(id, 'FULL'))
  const olId = data?.ol?.id

  const { data: isFinalCost } = useQuery({
    queryKey: ['final-cost-visibility', id],
    queryFn: () => getCenterFinalCostVisibility(id)
  })

  const mode = getCenterCostsMode({
    table: 'program-documents',
    costType: isFinalCost ? 'final-cost' : 'original-cost',
    role: user?.role,
    status: data?.costsStatus
  })

  const permissions = getOlPermissions({ type: data?.ol?.type, role: user.role })

  if (isPending) return <LoadingScreen />
  if (!data) return <ItemNotFound title="Le centre que vous cherchez n'existe pas" />

  return (
    <div className="mt-5 flex flex-col gap-3">
      <Typography variant="sub-heading" color={tailwindColors.black.DEFAULT}>
        {data?.ol?.type === OLTypeEnum.RESIDENCE
          ? t('ol.center.capsule.tabs.simple.program.and.center.address')
          : t('ol.center.capsule.tabs.simple.program.and.teaching.hall')}
      </Typography>

      <CenterCosts.ProgramDocuments centerId={id} mode={mode} olType={data?.ol?.type} />

      {view === 'addresses' ? (
        <CenterCosts.Addresses data={data} />
      ) : (
        <Box style="border" className="bg-white-hover gap-2">
          <div className="flex flex-row items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center bg-secondary-light rounded p-1">
              <BuildingTwoIcon color={tailwindColors.secondary.DEFAULT} />
            </div>

            <Typography variant="text">{t('center.costs.teaching.hall.title')}</Typography>
          </div>

          <OlPersonalSpaceTeachingHallTable
            centerId={id}
            olId={olId}
            canEdit={permissions['center-teachig-hall']}
          />
        </Box>
      )}
    </div>
  )
}
